import type { AgentField } from '@shared/api';
import {
  CloseSmallRedWithoutBg,
  EditSmallRed,
  PlusBlueWithoutBackground,
  RequiredMark,
} from '@shared/assets/icons/components';
import { Button, Flex, Typography } from 'antd';
import classNames from 'classnames';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styles from './fields.module.scss';
import { FieldCreateModal } from './ui/field-create/ui/field-create-modal';
import { useToggle } from '@shared/hooks/use-toggle';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { useAgentFieldDeleteAgentFieldDeleteMutation } from '@shared/api';

interface FieldsProps {
  agentFields: AgentField[];
  agentId: number;
  onDeleteField?: (field: AgentField) => void;
}

export const Fields: FC<FieldsProps> = ({ agentFields, agentId }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'prompt.fields' });
  const { Text: AntdText } = Typography;
  const { openConfirmModal } = useGlobalConfirmModalActions();
  const [deleteField] = useAgentFieldDeleteAgentFieldDeleteMutation();
  const {
    state: isCreateModalOpen,
    setTrue: openCreateModal,
    setFalse: closeCreateModal,
  } = useToggle();
  const [selectedField, setSelectedField] = useState<AgentField | null>(null);

  const activeFields = useMemo(() => agentFields.filter((field) => field.is_active), [agentFields]);
  const inactiveFields = useMemo(
    () => agentFields.filter((field) => !field.is_active),
    [agentFields],
  );

  // const handleActivate = (fieldId: number) => {
  //   const field = agentFields.find((item) => item.id === fieldId);
  //   if (!field) return;
  //   onActivateField?.({ ...field, is_active: true });
  // };

  // const handleDelete = (fieldId: number) => {
  //   const field = agentFields.find((item) => item.id === fieldId);
  //   if (!field) return;
  //   onDeleteField?.(field);
  // };
  const handleOpenConfirmModal = (field: AgentField) => {
    openConfirmModal({
      title: t('deleteModal.title'),
      text: (
        <Trans
          t={t}
          i18nKey="deleteModal.text"
          values={{
            field: field.name,
            interpolation: { escapeValue: false },
          }}
          components={{ bold: <AntdText strong /> }}
        />
      ),
      onConfirm: () => deleteField({ agentFieldId: field.id }),
    });
  };

  const handleAdd = () => {
    setSelectedField(null);
    openCreateModal();
  };

  const handleEdit = (field: AgentField) => {
    setSelectedField(field);
    openCreateModal();
  };

  return (
    <>
      <Flex vertical gap={8} className={styles.container}>
        <Flex wrap gap={8}>
          {activeFields.map((field) => (
            <Flex
              key={field.id}
              align="center"
              gap={6}
              className={classNames(styles.tag, styles.tagActive, {
                [styles.tagRequired]: field.is_required,
              })}
            >
              <span className={styles.tagContent}>
                {field.is_required && <RequiredMark className={styles.tagRequiredMark} />}
                {field.name}
              </span>

              <Button
                type="text"
                size="small"
                icon={<EditSmallRed color="#ED131C" width={16} height={16} />}
                className={styles.tagAction}
                onClick={() => handleEdit(field)}
              />

              <Button
                type="text"
                size="small"
                icon={<CloseSmallRedWithoutBg />}
                className={styles.tagAction}
                onClick={() => handleOpenConfirmModal(field)}
              />
            </Flex>
          ))}
        </Flex>

        <Flex wrap gap={8}>
          {inactiveFields.map((field) => (
            <Flex
              key={field.id}
              align="center"
              gap={6}
              className={classNames(styles.tagInactive, {
                [styles.tagRequired]: field.is_required,
              })}
            >
              <span className={styles.tagContent}>
                {field.is_required && <RequiredMark className={styles.tagRequiredMark} />}
                {field.name}
              </span>

              <Button
                type="text"
                size="small"
                icon={<EditSmallRed color="#ED131C" width={16} height={16} />}
                className={styles.tagAction}
                onClick={() => handleEdit(field)}
              />

              <Button
                type="text"
                size="small"
                icon={<CloseSmallRedWithoutBg />}
                className={styles.tagAction}
                onClick={() => handleOpenConfirmModal(field)}
              />
            </Flex>
          ))}

          <Button
            icon={<PlusBlueWithoutBackground color="var(--color-text-tertiary)" />}
            iconPosition="end"
            type="text"
            className={styles.addButton}
            onClick={handleAdd}
          >
            {t('addField')}
          </Button>
        </Flex>
      </Flex>

      <FieldCreateModal
        open={isCreateModalOpen}
        agentId={agentId}
        selectedField={selectedField}
        onClose={closeCreateModal}
      />
    </>
  );
};
